import type { RatingSnapshot } from '../types/workflow.js';

/**
 * The reviewer's exam-level mark on a practice item — a BINARY, not a scale.
 *
 * Owner decision 2026-07-30, superseding §4.2 of the authoring-agent brief (which asked for a 1–5
 * `exam_level` scale beside the model's band): *"have a simple option by the reviewer to mark it as
 * 'Exam level (may appear in simexams)' or 'Sub-exam level' — don't show or ask for any ratings
 * beyond that."* So: two choices, no scale, no band names, no draft-claims line, no numeric
 * difficulty anywhere (the band display was removed by AD-34 and must not return through this door),
 * and no academic/engagement block on practice items either.
 *
 * ## The stored encoding, in one place
 *
 * The ratings API is the transport (`POST /items/:id/rating`) and it is target-type agnostic, but its
 * validator is NOT value-agnostic: `rating-validator.ts` requires every value to be an **integer
 * from 1 to 5** (`Number.isInteger(value) && value >= 1 && value <= 5`), so a boolean cannot be sent.
 * The binary is therefore encoded at the two ENDS of that range:
 *
 * | reviewer sees | stored `exam_level` |
 * |---|---|
 * | Exam level (may appear in simexams) | **5** |
 * | Sub-exam level | **1** |
 * | (not marked) | key absent |
 *
 * The ends, not 1 and 2, so the ordering survives being read by anything that assumes the 1–5 space
 * the API enforces. **The number is never shown to the reviewer** — the labels are the interface, and
 * this table is the only place the mapping exists. Do not duplicate it.
 */
export type ExamLevelChoice = 'exam' | 'sub';

export const EXAM_LEVEL_KEY = 'exam_level';

export interface ExamLevelOption {
  choice: ExamLevelChoice;
  /** Reviewer-facing label. The parenthetical is deliberate: it says what the mark DOES. */
  label: string;
  /** The stored integer — see the table above. */
  value: number;
}

export const EXAM_LEVEL_OPTIONS: readonly ExamLevelOption[] = [
  { choice: 'exam', label: 'Exam level (may appear in simexams)', value: 5 },
  { choice: 'sub', label: 'Sub-exam level', value: 1 },
];

/**
 * The mark stored on an item, or `null` when the reviewer has not marked it.
 *
 * Decoded EXACTLY, not by threshold. A threshold (`value >= 3`) would read 3 and 4 — values this UI
 * never writes, but the target-type-agnostic ratings API would happily accept — as "exam level", i.e.
 * it would invent a decision that grants simexam eligibility from a value nobody chose. Anything
 * outside the table is treated as unmarked, which fails toward "not reviewed"
 * (gpt-5.6-sol, 2026-07-30).
 */
export function examLevelFromRatings(ratings: Readonly<Record<string, number>> | undefined): ExamLevelChoice | null {
  const value = ratings?.[EXAM_LEVEL_KEY];
  return EXAM_LEVEL_OPTIONS.find((option) => option.value === value)?.choice ?? null;
}

/** The request body fragment for a choice. */
export function examLevelRating(choice: ExamLevelChoice): Record<string, number> {
  const option = EXAM_LEVEL_OPTIONS.find((candidate) => candidate.choice === choice);
  if (!option) throw new Error(`Unknown exam-level choice: ${choice}`);
  return { [EXAM_LEVEL_KEY]: option.value };
}

/**
 * Whether a stored mark refers to content that has since changed.
 *
 * Ratings bind to the `patchHash` of the patch they were given for, and four paths can move the
 * effective patch out from under one: approve-with-edit, restore, plain-approve-from-cancelled, and
 * a read-modify-write race in `rate()` with no expected-hash condition. A binary mark is no more
 * immune to that than a 1–5 score is — "this is exam level" is a statement about a specific stem and
 * key — so it must show as stale, in the same way `MicroLessonReview` already shows a stale lesson
 * rating. This is the same computation, not a second mechanism.
 *
 * (What is NOT a hazard: a re-import overwriting the item under its mark. Every import insert is a
 * per-item conditional put with `attribute_not_exists(workItemId)`, so a collision fails the run
 * rather than replacing content — verified 2026-07-30, `AUTHORING-BRIEF-VERIFICATION-2026-07-30.md`.)
 */
export function examLevelIsStale(
  snapshot: Pick<RatingSnapshot, 'patchHash'> | undefined,
  currentPatchHash: string | undefined,
): boolean {
  if (!snapshot || !currentPatchHash) return false;
  return snapshot.patchHash !== currentPatchHash;
}
